import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import {styled} from "@mui/material/styles";
import {memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {WindowVirtualizer, type WindowVirtualizerHandle} from "virtua";

import SearchListingScrollbar from "./SearchListingScrollbar";

// Approximate height of one search result card, used as the size hint for
// rows the virtualizer has not measured yet. Overridable per listing since
// legacy strip cards and unified project cards differ considerably.
const defaultEstimatedRowHeightPx = 150;

// Extra pixels that are rendered before and after the viewport.
// The same distance expands the reported range for page prefetching.
const bufferSizePx = 1000;

// Delay before a failed page fetch becomes eligible again, so transient
// errors are retried without hammering the search backend in a tight loop.
const failedPageRetryDelayMs = 5000;

// Inside the virtualizer each row lives in its own wrapper, so the unified
// card's sibling-margin spacing never applies; the gap is applied as wrapper
// padding instead so the virtualizer measures it.
const RowWrapper = styled("div", {shouldForwardProp: (prop) => prop !== "gapPx"})<{gapPx: number}>(({gapPx}) => ({
    // Contain child margins so they can't collapse out of the row: the
    // virtualizer measures each row's border box, and escaped margins would
    // make rows overlap.
    display: "flow-root",
    paddingTop: gapPx,
    // Cards render as <li> elements; outside the classic <ul> the browser
    // would otherwise paint its default list markers beside each row.
    listStyle: "none",
    "& > li": {listStyle: "none"},
}));

/**
 * Skeleton stand-in for a not-yet-fetched search result card, matching the
 * legacy card's layout (thumb, title, description, meta line) so unloaded
 * rows read as loading cards rather than blank space.
 */
export function SearchResultCardSkeleton() {
    return (
        <>
            <Paper square component="li" sx={{p: 2, listStyle: "none"}}>
                <Box sx={{display: "flex", alignItems: "center"}}>
                    <Box sx={{pr: 2}}>
                        <Skeleton variant="rounded" width={64} height={64} />
                    </Box>
                    <Box sx={{flexGrow: 1, minWidth: 0}}>
                        <Skeleton variant="text" sx={{fontSize: "1.25rem"}} width="40%" />
                        <Skeleton variant="text" width="80%" />
                        <Skeleton variant="text" width="60%" />
                    </Box>
                </Box>
            </Paper>
            <Divider />
        </>
    );
}

export interface VirtualizedSearchResultsProps<Row> {
    /** Total number of hits reported by the first Typesense response. */
    totalHits: number;
    /** Hits per fetched page (the classic listing's page size). */
    perPage: number;
    /** 1-based page the classic listing had already fetched, seeding the list. */
    initialPage: number;
    /** The already-fetched rows for `initialPage`. */
    initialRows: readonly Row[];
    /**
     * Fetches one 1-based page of rows. May return fewer than `perPage` rows
     * (client-side filtering); already-fetched pages are never re-requested.
     */
    fetchPage: (page: number) => Promise<readonly Row[]>;
    renderRow: (row: Row) => ReactNode;
    /** Skeleton matching the listing's card type; defaults to the legacy strip skeleton. */
    renderSkeleton?: () => ReactNode;
    /** Size hint for unmeasured rows, matching the listing's card type. */
    estimatedRowHeightPx?: number;
    /** Vertical gap between rows (the unified card's sibling-margin spacing). */
    rowGapPx?: number;
    /** Whether a list-aligned browser scrollbar replaces the page scrollbar. */
    customScrollbar: boolean;
}

/**
 * Window-scrolled virtualized listing spanning the full search result set:
 * only the rows near the viewport are mounted, unloaded pages render as
 * skeleton cards at their positions, and the visible row range drives
 * on-demand page fetching (replacing the classic Previous/Next pagination).
 */
function VirtualizedSearchResults<Row>(props: VirtualizedSearchResultsProps<Row>) {
    const {
        totalHits,
        perPage,
        initialPage,
        initialRows,
        fetchPage,
        renderRow,
        renderSkeleton,
        estimatedRowHeightPx = defaultEstimatedRowHeightPx,
        rowGapPx = 0,
        customScrollbar,
    } = props;

    const pageCount = Math.max(1, Math.ceil(totalHits / perPage));
    const [rowsByPage, setRowsByPage] = useState<ReadonlyMap<number, readonly Row[]>>(() =>
        // A ?page= beyond the real page count fetches no rows; seeding that
        // empty result would mark a real page as loaded-but-blank forever.
        initialPage >= 1 && initialPage <= pageCount && initialRows.length > 0
            ? new Map([[initialPage, initialRows]])
            : new Map(),
    );
    // Pages currently being fetched, so a range report never duplicates an
    // in-flight request.
    const inFlightPagesRef = useRef(new Set<number>());
    // Bumped when a failed page becomes retryable, re-running the range
    // report so pages still near the viewport are re-requested.
    const [retryTick, setRetryTick] = useState(0);

    const virtualizerRef = useRef<WindowVirtualizerHandle>(null);

    // Restore a deep-linked ?page= position: the virtualizer mounts at the
    // top, so jump to the seeded page's first row once on mount.
    const didRestoreInitialPageRef = useRef(false);
    useEffect(() => {
        if (didRestoreInitialPageRef.current) return;
        didRestoreInitialPageRef.current = true;
        if (initialPage > 1 && initialPage <= pageCount) {
            virtualizerRef.current?.scrollToIndex((initialPage - 1) * perPage, {align: "start"});
        }
    }, [initialPage, pageCount, perPage]);

    // One entry per rendered row: the loaded rows of fetched pages at their
    // positions, and per-page skeleton runs for unfetched pages (sized by the
    // page's expected hit count, so the scroll span approximates the full
    // result set even while pages load out of order).
    const {rows, pageByRowIndex} = useMemo(() => {
        const elements: ReactNode[] = [];
        const pages: number[] = [];
        const push = (element: ReactNode, key: string, page: number) => {
            elements.push(
                <RowWrapper key={key} gapPx={elements.length === 0 ? 0 : rowGapPx}>
                    {element}
                </RowWrapper>,
            );
            pages.push(page);
        };
        for (let page = 1; page <= pageCount; page++) {
            const loaded = rowsByPage.get(page);
            if (loaded) {
                for (const [i, row] of loaded.entries()) {
                    push(renderRow(row), `row-${page}-${i}`, page);
                }
            } else {
                const expected = page === pageCount ? totalHits - (pageCount - 1) * perPage : perPage;
                for (let i = 0; i < expected; i++) {
                    push(
                        renderSkeleton ? renderSkeleton() : <SearchResultCardSkeleton />,
                        `skeleton-${page}-${i}`,
                        page,
                    );
                }
            }
        }
        return {rows: elements, pageByRowIndex: pages};
    }, [rowsByPage, pageCount, perPage, totalHits, renderRow, renderSkeleton, rowGapPx]);

    const requestPage = useCallback(
        (page: number) => {
            if (rowsByPage.has(page) || inFlightPagesRef.current.has(page)) return;
            inFlightPagesRef.current.add(page);
            void fetchPage(page)
                .then((fetched) => {
                    setRowsByPage((previous) => new Map(previous).set(page, fetched));
                })
                .then(() => {
                    inFlightPagesRef.current.delete(page);
                })
                .catch((err: unknown) => {
                    // eslint-disable-next-line no-console
                    console.error(`search page ${page} fetch failed:`, err);
                    // Keep the page marked in-flight through a cooldown, then
                    // make it requestable again and re-report the range so a
                    // viewer still parked on its skeletons triggers a retry.
                    setTimeout(() => {
                        inFlightPagesRef.current.delete(page);
                        setRetryTick((tick) => tick + 1);
                    }, failedPageRetryDelayMs);
                });
        },
        [rowsByPage, fetchPage],
    );

    // Fetch the pages covering the rows near the viewport. Reported on mount,
    // whenever the rendered rows change, and when scrolling stops — not on
    // every scroll frame, so a fast fling only fetches the landing position.
    const reportRenderedRange = useCallback(() => {
        const virtualizer = virtualizerRef.current;
        if (!virtualizer || pageByRowIndex.length === 0) return;
        const startRow = virtualizer.findItemIndex(Math.max(0, virtualizer.scrollOffset - bufferSizePx));
        const endRow = virtualizer.findItemIndex(virtualizer.scrollOffset + virtualizer.viewportSize + bufferSizePx);
        const firstPage = pageByRowIndex[Math.max(0, Math.min(startRow, pageByRowIndex.length - 1))];
        const lastPage = pageByRowIndex[Math.max(0, Math.min(endRow, pageByRowIndex.length - 1))];
        if (firstPage === undefined || lastPage === undefined) return;
        for (let page = firstPage; page <= lastPage; page++) {
            requestPage(page);
        }
    }, [pageByRowIndex, requestPage]);

    useEffect(() => {
        reportRenderedRange();
    }, [reportRenderedRange, retryTick]);

    return (
        <Box component="div" sx={{position: "relative"}} aria-live="polite">
            {customScrollbar && <SearchListingScrollbar />}
            <WindowVirtualizer
                ref={virtualizerRef}
                bufferSize={bufferSizePx}
                itemSize={estimatedRowHeightPx}
                onScrollEnd={reportRenderedRange}
            >
                {rows}
            </WindowVirtualizer>
        </Box>
    );
}

export default memo(VirtualizedSearchResults) as typeof VirtualizedSearchResults;
